import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../../components/common/ui/Modal';
import type { DraftItem } from '../types.local';
import { useToast } from 'src/shared/ui/toast/useToast';

type Props = {
    open: boolean;
    onClose: () => void;
    parent: DraftItem | null;
    siblings: DraftItem[];
    precision?: 4 | 5; // 저장 소수 자릿수 선택
    onChange: (nodeId: string, patch: Partial<DraftItem>) => void;
    onSaveSingleItem: (nodeId: string, payload: Partial<DraftItem>) => Promise<void>;
};

export default function RelativeWeightModal({
    open,
    onClose,
    parent,
    siblings,
    precision = 4,
    onChange,
    onSaveSingleItem,
}: Props) {
    const toast = useToast();

    const [relPercents, setRelPercents] = useState<number[]>([]);
    const [absWeights, setAbsWeights] = useState<number[]>([]); // ← 추가: 직접 입력 가능 actualWeight
    const factor = useMemo(() => Math.pow(10, precision), [precision]);

    const parentW = useMemo(() => {
        if (!parent) return 100;

        const v = Number(parent.weight);
        return isFinite(v) ? v : null;
    }, [parent]);

    // ===== helpers =====
    const roundP = (v: number) => Math.round(v); // %는 정수
    const roundW = (v: number) => Math.round(v * factor) / factor; // weight는 precision 자릿수

    const equalizeTo100 = (n: number) => {
        const base = Math.floor(100 / n);
        const rem = 100 - base * n;
        return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
    };

    const normalizeTo100 = (arr: number[]) => {
        const safe = arr.map((x) => Math.max(0, isFinite(x) ? x : 0));
        const sum = safe.reduce((a, b) => a + b, 0);
        if (sum <= 0) return equalizeTo100(safe.length);

        const scaled = safe.map((x) => (x / sum) * 100);
        const floors = scaled.map((v) => Math.floor(v));
        let diff = 100 - floors.reduce((a, b) => a + b, 0);

        const order = scaled
            .map((v, i) => ({ i, frac: v - Math.floor(v) }))
            .sort((a, b) => b.frac - a.frac)
            .map((x) => x.i);

        const res = floors.slice();
        for (let k = 0; k < diff; k++) res[order[k % res.length]] += 1;
        return res;
    };

    const sumPercent = (arr: number[]) => arr.reduce((a, b) => a + (isFinite(b) ? b : 0), 0);
    const sumWeight = (arr: number[]) => arr.reduce((a, b) => a + (isFinite(b) ? b : 0), 0);

    // ===== init on open =====
    useEffect(() => {
        if (!open) return;
        if (parentW == null) return;

        // 초기 %는 현재 weight 기준(없으면 균등), absWeights는 현재 값(없으면 %로 환산)
        const initPercent = (() => {
            const perc = siblings.map((s) => {
                const w = typeof s.weight === 'number' ? s.weight : 0;
                return parentW > 0 ? Math.round((w / parentW) * 1000) / 10 : 0; // 소수1자리로 임시 계산
            });
            const sum = sumPercent(perc);
            return sum > 0 ? normalizeTo100(perc.map((p) => roundP(p))) : equalizeTo100(siblings.length);
        })();

        const initWeights = siblings.map((s, i) => {
            const w = typeof s.weight === 'number' ? s.weight : null;
            return w != null ? roundW(w) : roundW((parentW * initPercent[i]) / 100);
        });

        setRelPercents(initPercent);
        setAbsWeights(initWeights);
    }, [open, parentW, siblings]);

    // ％ → weight 동기화(행 단위)
    const updatePercentAt = (idx: number, p: number) => {
        if (parentW == null) return;
        const nextPerc = relPercents.map((x, i) => (i === idx ? Math.max(0, roundP(p)) : x));
        setRelPercents(nextPerc);
        setAbsWeights((prev) => prev.map((w, i) => (i === idx ? roundW((parentW * nextPerc[i]) / 100) : w)));
    };

    // weight → ％ 동기화(행 단위)
    const updateWeightAt = (idx: number, w: number) => {
        if (parentW == null) return;
        const nw = Math.max(0, Number.isFinite(w) ? w : 0);
        const nextW = absWeights.map((x, i) => (i === idx ? roundW(nw) : x));
        setAbsWeights(nextW);
        setRelPercents((prev) => prev.map((p, i) => (i === idx ? roundP((nextW[i] / parentW) * 100) : p)));
    };

    // 전체 actualWeight 비례 스케일(부모 weight로 합 맞추기)
    const scaleWeightsToParent = () => {
        if (parentW == null) return;
        const cur = sumWeight(absWeights);
        if (cur <= 0) return;
        const ratio = parentW / cur;
        const scaled = absWeights.map((w) => roundW(w * ratio));
        setAbsWeights(scaled);
        setRelPercents(scaled.map((w) => roundP((w / parentW) * 100)));
    };

    // 확정(저장)
    const apply = async () => {
        if (parentW == null) return;

        // 최종 검증: weight 합이 부모 weight와 precision 허용 오차 이내인가?
        const totalW = sumWeight(absWeights);
        const eps = 1 / factor; // 허용 오차
        if (Math.abs(totalW - parentW) > eps) {
            toast.warning(
                `자식 actualWeight 합(${totalW})이 부모 actualWeight(${parentW})와 일치하지 않습니다.\n부모 weight로 합 맞추기 버튼으로 보정한 뒤 저장해 주세요.`,
                {
                    title: '저장 불가',
                    duration: 4500,
                },
            );
            return;
        }

        // 저장(행별 weight만 저장)
        await Promise.all(
            siblings.map((sib, idx) => {
                const nextWeight = roundW(absWeights[idx]);
                onChange(sib.nodeId!, { weight: nextWeight });
                return onSaveSingleItem(sib.nodeId!, { weight: nextWeight });
            }),
        );

        onClose();
    };

    return (
        <Modal size="2xl" open={open} title="형제별 상대 가중치(%) / 실제 가중치 입력" onClose={onClose}>
            {parentW == null ? (
                <div className="text-sm text-rose-600">
                    부모의 weight가 없습니다. 먼저 부모 항목에 weight를 입력/저장한 뒤 다시 시도하세요.
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="text-sm text-gray-600">
                        부모: <span className="font-mono">{parent?.name ?? '(최상위 / 부모 없음)'}</span>
                        <span className="ml-2 text-xs text-gray-500">
                            기준 부모 절대 가중치: <b>{parentW}</b>
                        </span>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left">형제 이름</th>
                                    <th className="px-3 py-2 text-right">상대 가중치(%)</th>
                                    <th className="px-3 py-2 text-right">actualWeight</th>
                                </tr>
                            </thead>
                            <tbody>
                                {siblings.map((s, idx) => (
                                    <tr key={s.nodeId} className="border-t">
                                        <td className="px-3 py-2">{s.name}</td>
                                        <td className="px-3 py-2 text-right">
                                            <input
                                                type="number"
                                                className="border rounded px-2 py-1 w-24 text-right"
                                                min={0}
                                                step={1}
                                                value={relPercents[idx] ?? 0}
                                                onChange={(e) => updatePercentAt(idx, Number(e.target.value) || 0)}
                                            />{' '}
                                            %
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <input
                                                type="number"
                                                className="border rounded px-2 py-1 w-32 text-right"
                                                min={0}
                                                step={1 / factor}
                                                value={Number.isFinite(absWeights[idx]) ? absWeights[idx] : 0}
                                                onChange={(e) => updateWeightAt(idx, Number(e.target.value))}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td className="px-3 py-2 text-right font-medium">합계</td>
                                    <td className="px-3 py-2 text-right font-mono">{sumPercent(relPercents)}%</td>
                                    <td className="px-3 py-2 text-right font-mono">{roundW(sumWeight(absWeights))}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                            * precision: 소수 {precision}자리(허용 오차 ±{1 / factor})
                        </span>
                        <div className="flex-1" />
                        <button
                            className="px-3 py-1 text-sm rounded border hover:bg-gray-50"
                            title="합이 100 되도록 균등 분배"
                            onClick={() => {
                                const next = equalizeTo100(siblings.length);
                                setRelPercents(next);
                                setAbsWeights(next.map((p) => roundW((parentW * p) / 100)));
                            }}
                        >
                            균등배분
                        </button>
                        <button
                            className="px-3 py-1 text-sm rounded border hover:bg-gray-50"
                            title="입력된 % 합을 100%로 정규화"
                            onClick={() => {
                                const next = normalizeTo100(relPercents);
                                setRelPercents(next);
                                setAbsWeights(next.map((p) => roundW((parentW * p) / 100)));
                            }}
                        >
                            100% 보정
                        </button>
                        <button
                            className="px-3 py-1 text-sm rounded border hover:bg-gray-50"
                            title="actualWeight 전체를 비례 스케일하여 부모 weight와 합이 정확히 같도록 보정"
                            onClick={scaleWeightsToParent}
                        >
                            부모 weight로 합 맞추기
                        </button>
                        <button
                            className="px-3 py-1 text-sm rounded border border-green-500 text-green-600 hover:bg-green-50"
                            onClick={apply}
                            title="행별 actualWeight를 저장"
                        >
                            확정
                        </button>
                    </div>

                    <div className="mt-1 text-xs text-gray-500">
                        * ％는 정수로 입력합니다. actualWeight는 소수 {precision}자리까지 입력 가능.
                        <br />
                        * ％ 또는 actualWeight 어느 쪽을 수정해도 서로 자동 환산됩니다.
                        <br />* 저장 전 실제 합계가 반드시 부모 actualWeight와 일치해야 합니다(허용 오차 ±{1 / factor}).
                    </div>
                </div>
            )}
        </Modal>
    );
}
